import React, { useEffect, useState, useRef, useCallback } from "react";
import Layout from "./Layout";
import UserCard from "../ui/compoP/UserCard";
import SvgIcn from "../data/IconCompo";
import { getItems } from "../services/itemsServices";
import { getFavoritesByUser, getMyFavorites } from "../services/pbFavorites";
import {
  resolveFavoriteItems,
  findPocketBaseFavoriteId,
  favoriteIdMatches,
} from "../services/favoriteResolver";
import { LazyItemCard } from "../ui/compoP/LazyItemCard";
import { useParams } from "react-router-dom";
import capitalizeFirstLetter from "../utils/Capitalize";
import {
  currentUser,
  isAuthenticated,
  getFileUrl,
  ensureAuthHydrated,
} from "../auth/authApi";
import { getUserFavs } from "../services/UserService";
import { loadPublicProfile } from "../services/profilePublic";
import ConfirmEmailChange from "./ConfirmEmailChange";
import NotFound from "./NotFound";

function looksLikeJwt(token) {
  if (typeof token !== "string") return false;
  const t = token.trim();
  if (t.length < 40) return false;
  const parts = t.split(".");
  return parts.length === 3 && parts.every((p) => p.length > 5);
}

function Profile() {
  const [userData, setUserData] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [image, setImage] = useState(null);
  const [data, setData] = useState(null);
  /** Raw PB favorite ids per bucket (for toggling legacy rows). */
  const [favGrouped, setFavGrouped] = useState(null);
  /** Current viewer's favorites (for star state on someone else's profile). */
  const [viewerFavGrouped, setViewerFavGrouped] = useState(null);
  const { username } = useParams();
  const isEmailChangeToken = looksLikeJwt(username);
  const [selectedMode, setSelectedMode] = useState("tools");
  const contentRef = useRef(null);
  const toolsRef = useRef(null);
  const resosRef = useRef(null);
  const ideazRef = useRef(null);
  const appsRef = useRef(null);

  /** Scroll spy: `.content-wrapper` scrolls, not `.profile-favs-box`. */
  const updateActiveSection = useCallback(() => {
    const scroller = contentRef.current;
    if (!scroller) return;

    const { scrollTop, clientHeight, scrollHeight } = scroller;
    const scrollable = scrollHeight > clientHeight + 16;
    const nearBottom =
      scrollable && scrollTop + clientHeight >= scrollHeight - 8;

    if (nearBottom) {
      setSelectedMode("apps");
      return;
    }

    const cr = scroller.getBoundingClientRect();
    const markerY = cr.top + 72;
    let active = "tools";
    const pairs = [
      ["tools", toolsRef],
      ["resos", resosRef],
      ["ideaz", ideazRef],
      ["apps", appsRef],
    ];
    for (const [mode, r] of pairs) {
      const el = r.current;
      if (!el) continue;
      if (el.getBoundingClientRect().top <= markerY) active = mode;
    }
    setSelectedMode(active);
  }, []);

  useEffect(() => {
    const scroller = contentRef.current;
    if (!scroller) return;
    const onScroll = () => updateActiveSection();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    const raf = requestAnimationFrame(() => updateActiveSection());
    return () => {
      cancelAnimationFrame(raf);
      scroller.removeEventListener("scroll", onScroll);
    };
  }, [data, updateActiveSection]);

  const scrollToSection = (mode) => {
    setSelectedMode(mode);
    const refs = {
      tools: toolsRef,
      resos: resosRef,
      ideaz: ideazRef,
      apps: appsRef,
    };
    refs[mode]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    requestAnimationFrame(() => updateActiveSection());
  };

  useEffect(() => {
    ensureAuthHydrated().catch(() => {});
  }, []);

  useEffect(() => {
    if (isEmailChangeToken) return;
    let cancelled = false;
    setProfileError(null);
    setUserData(null);

    (async () => {
      try {
        const record = await loadPublicProfile(username);
        if (!cancelled) {
          setUserData(record);
          setProfileError(null);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        if (!cancelled) {
          setUserData(null);
          setProfileError(
            "This profile could not be loaded. The user may not exist, or the profile API is unavailable."
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [username, isEmailChangeToken]);

  useEffect(() => {
    if (!userData) {
      setImage(null);
      return;
    }
    const raw = userData.profile_img || userData.profileImg;
    if (typeof raw === "string" && /^https?:\/\//i.test(raw.trim())) {
      setImage(raw.trim());
      return;
    }
    const url = getFileUrl(userData, userData?.avatar);
    setImage(url || null);
  }, [userData]);

  useEffect(() => {
    if (!userData) return;

    let cancelled = false;

    (async () => {
      try {
        await ensureAuthHydrated();
        if (cancelled) return;

        const me = currentUser();
        const ownProfile =
          isAuthenticated() &&
          me?.username &&
          userData?.username &&
          String(me.username).toLowerCase() ===
            String(userData.username).toLowerCase();

        let grouped;
        if (ownProfile) {
          grouped = await getMyFavorites();
        } else if (userData.id) {
          grouped = await getFavoritesByUser(userData.id);
        } else if (userData.legacyMongoId) {
          try {
            const res = await getUserFavs(userData.legacyMongoId);
            const d = res?.data || {};
            const asIds = (arr) =>
              Array.isArray(arr)
                ? arr.map((x) =>
                    x && typeof x === "object" && x._id != null ? x._id : x
                  )
                : [];
            grouped = {
              myTools: asIds(d.myTools),
              myResos: asIds(d.myResos),
              myIdeaz: asIds(d.myIdeaz),
              myApps: asIds(d.myApps),
            };
          } catch {
            grouped = {
              myTools: [],
              myResos: [],
              myIdeaz: [],
              myApps: [],
            };
          }
        } else {
          grouped = {
            myTools: [],
            myResos: [],
            myIdeaz: [],
            myApps: [],
          };
        }

        if (cancelled) return;

        const modes = [
          { key: "myTools", mode: "tools" },
          { key: "myResos", mode: "resos" },
          { key: "myIdeaz", mode: "ideaz" },
          { key: "myApps", mode: "apps" },
        ];
        const entries = await Promise.all(
          modes.map(async ({ key, mode }) => {
            const ids = grouped[key] || [];
            if (ids.length === 0) return [key, []];
            const result = await getItems(mode, "ALL", "", [], [], 1, 1000);
            const list = result.items || [];
            const resolved = await resolveFavoriteItems(ids, list);
            return [key, resolved];
          })
        );
        const resultBuckets = Object.fromEntries(entries);

        if (cancelled) return;
        setData(resultBuckets);
        setFavGrouped(grouped);
      } catch (err) {
        console.warn("Profile favorites load failed", err);
        if (!cancelled) {
          setData({ myTools: [], myResos: [], myIdeaz: [], myApps: [] });
          setFavGrouped(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userData]);

  useEffect(() => {
    if (!userData) {
      setViewerFavGrouped(null);
      return;
    }
    const me = currentUser();
    const own =
      isAuthenticated() &&
      me?.username &&
      userData?.username &&
      String(me.username).toLowerCase() ===
        String(userData.username).toLowerCase();
    if (own || !isAuthenticated()) {
      setViewerFavGrouped(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await ensureAuthHydrated();
        const g = await getMyFavorites();
        if (!cancelled) setViewerFavGrouped(g);
      } catch {
        if (!cancelled) setViewerFavGrouped(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const categoryIds = {
    "64761102ba22fed734b9de7a": "tools",
    "64761222ba22fed734b9de7d": "resos",
    "64761240ba22fed734b9de81": "ideaz",
    "65a2912599ed3cb8b9886940": "apps",
  };

  const categoryIdFromItem = (item) => {
    const raw = item?.category_id;
    const cid = Array.isArray(raw) ? raw[0] : raw;
    return typeof cid === "string" ? cid : "";
  };

  const meForOwn = currentUser();
  const ownProfile =
    !!userData &&
    isAuthenticated() &&
    meForOwn?.username &&
    userData?.username &&
    String(meForOwn.username).toLowerCase() ===
      String(userData.username).toLowerCase();

  const isStarOnForViewer = (item, mode) => {
    if (ownProfile) return true;
    if (!isAuthenticated()) return false;
    const key = `my${capitalizeFirstLetter(mode)}`;
    const ids = viewerFavGrouped?.[key] || [];
    return ids.some((fid) => favoriteIdMatches(fid, item));
  };

  const favIdsForToggle = (mode) =>
    (ownProfile ? favGrouped : viewerFavGrouped)?.[
      `my${capitalizeFirstLetter(mode)}`
    ] || [];

  const toogleLike = (item) => {
    const me = currentUser();
    if (isAuthenticated() && me?.username === userData?.username) {
      const cid = categoryIdFromItem(item);
      if (cid && Object.prototype.hasOwnProperty.call(categoryIds, cid)) {
        const cat = categoryIds[cid];
        const bucketKey = `my${capitalizeFirstLetter(cat)}`;
        setData((prev) => {
          const items = (prev[bucketKey] || []).filter((el) => el._id !== item._id);
          return { ...prev, [bucketKey]: items };
        });
        setFavGrouped((prev) => {
          if (!prev) return prev;
          const rawIds = prev[bucketKey] || [];
          const pbKey =
            item.__pbFavoriteKeys?.[0] ?? findPocketBaseFavoriteId(rawIds, item);
          if (!pbKey) return prev;
          return {
            ...prev,
            [bucketKey]: rawIds.filter((id) => id !== pbKey),
          };
        });
      }
    } else if (userData && isAuthenticated()) {
      const cid = categoryIdFromItem(item);
      if (!cid || !Object.prototype.hasOwnProperty.call(categoryIds, cid)) return;
      const cat = categoryIds[cid];
      const bucketKey = `my${capitalizeFirstLetter(cat)}`;
      setViewerFavGrouped((prev) => {
        if (!prev) return prev;
        const ids = [...(prev[bucketKey] || [])];
        const wasOn = ids.some((fid) => favoriteIdMatches(fid, item));
        if (wasOn) {
          return {
            ...prev,
            [bucketKey]: ids.filter((fid) => !favoriteIdMatches(fid, item)),
          };
        }
        return { ...prev, [bucketKey]: [...ids, item._id] };
      });
      setTimeout(() => {
        getMyFavorites().then(setViewerFavGrouped).catch(() => {});
      }, 450);
    }
  };
  const socialLinks = {};
  userData?.mediaLinks?.forEach((link) => {
    socialLinks[link.type] = link.link;
  });

  const profileSocialOrder = (() => {
    const keys = [
      "figma",
      "dribbble",
      "behance",
      "linkedin",
      "x",
      "instagram",
      "facebook",
      "tiktok",
    ];
    const out = [];
    const seen = new Set();
    userData?.mediaLinks?.forEach((link) => {
      const t = String(link.type || "").toLowerCase();
      const key = t === "twitter" ? "x" : t;
      if (!keys.includes(key) || !link.link) return;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(key);
    });
    return out;
  })();

  // Safety net: if PocketBase action URL is misconfigured to "/{TOKEN}",
  // the app routes here (/:username). Detect JWT-like tokens and run the
  // email-change confirmation flow instead of trying to load a profile.
  if (isEmailChangeToken) {
    return <ConfirmEmailChange token={username} />;
  }

  if (profileError) {
    return <NotFound />;
  }

  return (
    <Layout
      contentRef={contentRef}
      centerBar={
        <>
          <UserCard
            data={{
              ...userData,
              image: image,
              subRole: userData?.sub_role,
              figma: socialLinks.figma,
              twitter: socialLinks.x,
              dribbble: socialLinks.dribbble,
              behance: socialLinks.behance,
              linkedin: socialLinks.linkedin,
              instagram: socialLinks.instagram,
              facebook: socialLinks.facebook,
              tiktok: socialLinks.tiktok,
            }}
            visibleInputs={{
              figma: true,
              twitter: true,
              dribbble: true,
              behance: true,
              linkedin: true,
              instagram: true,
              facebook: true,
              tiktok: true,
            }}
            socialOrder={profileSocialOrder}
          />
          <div className="profile-nav-box">
            <div
              className={`profile-nav-button ${
                selectedMode == "tools" && "selected"
              }`}
              onClick={() => scrollToSection("tools")}
            >
              <SvgIcn name={"tool"} className={""} />
              <p>{data?.myTools?.length}</p>
            </div>
            <div
              className={`profile-nav-button ${
                selectedMode == "resos" && "selected"
              }`}
              onClick={() => scrollToSection("resos")}
            >
              <SvgIcn name={"resos"} className={""} />
              <p>{data?.myResos?.length}</p>
            </div>
            <div
              className={`profile-nav-button ${
                selectedMode == "ideaz" && "selected"
              }`}
              onClick={() => scrollToSection("ideaz")}
            >
              <SvgIcn name={"idea"} className={""} />
              <p>{data?.myIdeaz?.length}</p>
            </div>
            <div
              className={`profile-nav-button ${
                selectedMode == "apps" && "selected"
              }`}
              onClick={() => scrollToSection("apps")}
            >
              <SvgIcn name={"apps"} className={""} />
              <p>{data?.myApps?.length}</p>
            </div>
          </div>
        </>
      }
    >
      <div className="profile-favs-box">
        <div className="favs-title" ref={toolsRef}>
          <SvgIcn name={"tool"} className={""} />
          <h3>
            Tools <span>{data?.myTools?.length}</span>
          </h3>
        </div>
        <div className="cards-grid">
          {!data?.myTools || data?.myTools?.length == 0 ? (
            <div className="not-found-favs-box">
              {" "}
              <SvgIcn name="logo06" className="not-found-favs-icon" />
              <p>
                No Tool <br /> Fav Saved!
              </p>
            </div>
          ) : (
            data?.myTools?.map((item, index) => (
              <LazyItemCard
                key={item._id || index}
                item={item}
                selectedMode={"tools"}
                page={"profile"}
                isFavorite={isStarOnForViewer(item, "tools")}
                favoriteToggleId={findPocketBaseFavoriteId(
                  favIdsForToggle("tools"),
                  item
                )}
                toogleLike={toogleLike}
              />
            ))
          )}
        </div>
        <div className="favs-title" ref={resosRef}>
          <SvgIcn name={"resos"} className={""} />
          <h3>
            Resos <span>{data?.myResos?.length}</span>
          </h3>
        </div>
        <div className="cards-grid">
          {!data?.myResos || data?.myResos?.length === 0 ? (
            <div className="not-found-favs-box">
              {" "}
              <SvgIcn name="logo06" className="not-found-favs-icon" />
              <p>
                No Resource <br />
                Fav Saved!
              </p>
            </div>
          ) : (
            data?.myResos?.map((item, index) => (
              <LazyItemCard
                key={item._id || index}
                item={item}
                selectedMode={"resos"}
                page={"profile"}
                isFavorite={isStarOnForViewer(item, "resos")}
                favoriteToggleId={findPocketBaseFavoriteId(
                  favIdsForToggle("resos"),
                  item
                )}
                toogleLike={toogleLike}
              />
            ))
          )}
        </div>
        <div className="favs-title" ref={ideazRef}>
          <SvgIcn name={"idea"} className={""} />
          <h3>
            Ideaz <span>{data?.myIdeaz?.length}</span>
          </h3>
        </div>
        <div className="cards-grid">
          {!data?.myIdeaz || data?.myIdeaz?.length === 0 ? (
            <div className="not-found-favs-box">
              {" "}
              <SvgIcn name="logo06" className="not-found-favs-icon" />
              <p>
                No Inspiration <br />
                Fav Saved!
              </p>
            </div>
          ) : (
            data?.myIdeaz?.map((item, index) => (
              <LazyItemCard
                key={item._id || index}
                item={item}
                selectedMode={"ideaz"}
                page={"profile"}
                isFavorite={isStarOnForViewer(item, "ideaz")}
                favoriteToggleId={findPocketBaseFavoriteId(
                  favIdsForToggle("ideaz"),
                  item
                )}
                toogleLike={toogleLike}
              />
            ))
          )}
        </div>
        <div className="favs-title" ref={appsRef}>
          <SvgIcn name={"apps"} className={""} />
          <h3>
            Apps <span>{data?.myApps?.length}</span>
          </h3>
        </div>
        <div className="cards-grid">
          {!data?.myApps || data?.myApps?.length === 0 ? (
            <div className="not-found-favs-box">
              <SvgIcn name="logo06" className="not-found-favs-icon" />
              <p>
                No App <br />
                Fav Saved!
              </p>
            </div>
          ) : (
            data?.myApps?.map((item, index) => (
              <LazyItemCard
                key={item._id || index}
                item={item}
                selectedMode={"apps"}
                page={"profile"}
                isFavorite={isStarOnForViewer(item, "apps")}
                favoriteToggleId={findPocketBaseFavoriteId(
                  favIdsForToggle("apps"),
                  item
                )}
                toogleLike={toogleLike}
              />
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Profile;
